/* eslint-disable @typescript-eslint/no-magic-numbers */
import {
  Component,
  OnDestroy,
  ChangeDetectionStrategy,
  AfterViewInit,
  ChangeDetectorRef,
  WritableSignal,
  signal,
  effect,
} from '@angular/core';
import { MetaService } from '@app/shared/services/meta.service';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { WindowUtils } from '@app/shared/utils/window.util';
import { AkitaConfigurationQuery } from '@app/akita/configuration/state/configuration.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { FooterComponent } from '../shared/components/new-footer/footer.component';
import { BidiModule } from '@angular/cdk/bidi';
import { AsyncPipe } from '@angular/common';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { PopsyCheckboxComponent } from '@app/components/checkbox/popsy-checkbox.component';
import {
  QUARA_FAQ_DATA,
  TABBY_FAQ_DATA,
  TAMARA_FAQ_DATA,
  BASEETA_FAQ_DATA,
} from './installment-faq.data';
import { ChevronDownIconComponent } from '@app/shared/components/icons/generated/chevron-down/chevron-down.component';
import { QuaraIconComponent } from '@app/shared/components/icons/generated/quara/quara.component';
import { TamaraIconComponent } from '@app/shared/components/icons/generated/tamara/tamara.component';
import { TabbyIconComponent } from '@app/shared/components/icons/generated/tabby/tabby.component';
import { BaseetaBadgeEnIconComponent } from '@app/shared/components/icons/generated/baseeta-badge-en/baseeta-badge-en.component';
import { OtherTopicsComponent } from '@app/faq-page/components/other-topics/other-topics.view';
import {
  BASEETA_MAX_INSTALLMENTS,
  BASEETA_SUPPORTED_COUNTRIES,
  SHOW_BASEETA_PROMO,
} from '@app/checkout/payment-providers/baseeta/components/baseeta-promo/baseeta-promo.component';
import { InstallmentsCalculatorComponent } from '../components/installments-calculator/installments-calculator.component';
import { TamaraArIconComponent } from '../shared/components/icons/generated/tamara-ar/tamara-ar.component';
import { QuaraArIconComponent } from '../shared/components/icons/generated/quara-ar/quara-ar.component';
import { BaseetaBadgeArIconComponent } from '../shared/components/icons/generated/baseeta-badge-ar/baseeta-badge-ar.component';
import { TabbyArIconComponent } from '../shared/components/icons/generated/tabby-ar/tabby-ar.component';
import { CURRENCY_BY_COUNTRY } from '@app/shared/utils/locale.utils';
import { QUARA_MAX_INSTALLMENTS } from '@app/checkout/payment-providers/quara/components/quara-promo/quara-promo.component';

@Component({
  selector: 'app-installment-plans',
  templateUrl: './installment-plans.template.html',
  styleUrls: ['./installment-plans.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BidiModule,
    FooterComponent,
    AsyncPipe,
    TranslocoModule,
    PopsyCheckboxComponent,
    ChevronDownIconComponent,
    OtherTopicsComponent,
    QuaraIconComponent,
    TamaraIconComponent,
    TabbyIconComponent,
    BaseetaBadgeEnIconComponent,
    InstallmentsCalculatorComponent,
    TamaraArIconComponent,
    QuaraArIconComponent,
    BaseetaBadgeArIconComponent,
    TabbyArIconComponent,
  ],
})
export class InstallmentPlansComponent implements OnDestroy, AfterViewInit {
  public lang: string;
  public country: string;

  public lessThan$: Observable<ScreenSizeInfoState>;
  public inFullScreenMode$: Observable<boolean>;

  public quaraFaqData = QUARA_FAQ_DATA;
  public tamaraFaqData = TAMARA_FAQ_DATA;
  public tabbyFaqData = TABBY_FAQ_DATA;
  public baseetaFaqData = BASEETA_FAQ_DATA;

  public currency: string | null = null;

  public selectedTab: WritableSignal<string | null> = signal(null);
  public selectedCalculator: WritableSignal<string | null> = signal(null);
  public selectedMaxInstallments: WritableSignal<number | null> = signal(4);
  public calculatorAmount: WritableSignal<number | null> = signal(1500);

  public displayQuara: WritableSignal<boolean> = signal(false);
  public displayBaseeta: WritableSignal<boolean> = signal(false);

  private readonly langChangesObserver: Subscription | null;
  public readonly locationObserver: Subscription | null;

  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly meta: MetaService,
    private readonly translateService: TranslocoService,
    private readonly akitaConfigurationQuery: AkitaConfigurationQuery,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly detectChanges: ChangeDetectorRef
  ) {
    this.destroySubject = new Subject();

    this.lessThan$ = this.akitaScreenQuery.selectLessThan();

    this.inFullScreenMode$ = this.akitaRouterQuery.selectInFullScreenMode();

    this.selectedTab.set(null);
    this.selectedCalculator.set('tamara');

    this.country = this.akitaProductsQuery.validateLocale().country;
    this.currency = CURRENCY_BY_COUNTRY[this.country] || 'SAR';

    this.locationObserver = this.akitaProductsQuery
      .selectCountry()
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (country) => {
          this.displayQuara.set(this.shouldDisplayQuara(country));
          this.displayBaseeta.set(this.shouldDisplayBaseeta(country));
        },
      });

    this.langChangesObserver = this.translateService.langChanges$
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: () => {
          const description = this.translateService.translate('app_title');
          this.meta.setTitle('title_terms_of_use', true);
          this.meta.setCanonical();
          this.meta.setAlternateHreflangsTags(null);

          this.meta.setTags([
            { name: 'og:url', content: `${WindowUtils.getLocationHref() || ''}` },
            { name: 'og:type', content: `website` },
            { name: 'og:description', content: description },
            { name: 'description', content: description },
          ]);
        },
        error: () => {},
      });

    const languageCode = this.akitaConfigurationQuery.languageCode;
    this.lang = (languageCode.locale || 'EN').toUpperCase();

    effect(
      () => {
        if (this.selectedCalculator() === 'quara') {
          this.selectedMaxInstallments.set(QUARA_MAX_INSTALLMENTS);
          this.calculatorAmount.set(1500);
        }
        if (this.selectedCalculator() === 'tabby') {
          this.selectedMaxInstallments.set(4);
          this.calculatorAmount.set(1500);
        }
        if (this.selectedCalculator() === 'tamara') {
          this.selectedMaxInstallments.set(4);
          this.calculatorAmount.set(1500);
        }
        if (this.selectedCalculator() === 'baseeta') {
          this.selectedMaxInstallments.set(BASEETA_MAX_INSTALLMENTS);
          this.calculatorAmount.set(1500);
        }
      },
      { allowSignalWrites: true }
    );
  }

  public ngAfterViewInit(): void {
    this.country = this.akitaProductsQuery.country;
    this.detectChanges.detectChanges();
    this.detectChanges.markForCheck();
  }

  private shouldDisplayQuara(currentCountry?: string | null): boolean {
    return Boolean(currentCountry === 'SA');
  }

  private shouldDisplayBaseeta(currentCountry?: string | null): boolean {
    if (
      BASEETA_SUPPORTED_COUNTRIES.includes(currentCountry || '') &&
      SHOW_BASEETA_PROMO
    ) {
      this.selectedCalculator.set('baseeta');
    }
    return Boolean(
      BASEETA_SUPPORTED_COUNTRIES.includes(currentCountry || '') && SHOW_BASEETA_PROMO
    );
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.langChangesObserver) {
      this.langChangesObserver.unsubscribe();
    }
  }
}
